import { Button } from "@/components/ui/button";
import { Header } from "@/components/layout/Header";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import AuthProvider from "@/providers/auth.provider";
import fileProvider from "@/providers/file.provider";
import type { IOrganizationObject } from "@/interfaces/organization";
import { DragAndDropFileUploader } from "@/components/drag-and-drop/DragAndDropFileUploader";
import { FileText } from "lucide-react";
import { UploadedFileList } from "@/components/estimation-details/upload/UploadedFileList";
import type { File } from "@/interfaces/estimation.interface";
import type { Estimation } from "@/interfaces/estimation.interface";
import { collection, getDocs } from "firebase/firestore";
import { useFirestore } from "@/context/firebase-context";
import { useNavigate } from "react-router-dom";
import { CheckBox } from "@/components/CheckBox";
import { useSelector } from "react-redux";
import { appSelector } from "@/redux/selectors/app.selector";

export const ValueAddPage = () => {
  const [selectedOrganization, setSelectedOrganization] = useState<string>("");
  const [selectedEstimate, setSelectedEstimate] = useState<string>("");
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [actualFiles, setActualFiles] = useState<globalThis.File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [organizations, setOrganizations] = useState<IOrganizationObject[]>([]);
  const [estimates, setEstimates] = useState<Estimation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingEstimates, setIsLoadingEstimates] = useState(false);
  const [useMyOrganization, setUseMyOrganization] = useState(false);
  const navigate = useNavigate();
  const firestore = useFirestore();

  // Get user's organization data
  const userOrganizationId = useSelector(appSelector.organizationId);
  const userOrganizationName = useSelector(appSelector.organizationName);

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const response = await AuthProvider.listOrganizations();
        if (response.success) {
          setOrganizations(response.data.organizations);
        } else {
          toast.error("Failed to fetch organizations");
        }
      } catch (error) {
        console.error("Error fetching organizations:", error);
        toast.error("Failed to fetch organizations");
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrganizations();
  }, []);

  // Handle checkbox change
  useEffect(() => {
    if (useMyOrganization && userOrganizationId) {
      setSelectedOrganization(userOrganizationId);
    } else if (!useMyOrganization) {
      setSelectedOrganization("");
    }
  }, [useMyOrganization, userOrganizationId]);

  useEffect(() => {
    const fetchEstimates = async () => {
      const organizationToUse = useMyOrganization ? userOrganizationId : selectedOrganization;

      if (!organizationToUse) {
        setEstimates([]);
        setSelectedEstimate("");
        return;
      }

      setIsLoadingEstimates(true);
      try {
        const estimatesCollection = collection(firestore, `organizations/${organizationToUse}/estimates`);
        const snapshot = await getDocs(estimatesCollection);
        const estimatesData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Estimation[];

        setEstimates(estimatesData);
        setSelectedEstimate(""); // Reset selected estimate when organization changes
      } catch (error) {
        console.error("Error fetching estimates:", error);
        toast.error("Failed to fetch estimates");
        setEstimates([]);
      } finally {
        setIsLoadingEstimates(false);
      }
    };

    fetchEstimates();
  }, [selectedOrganization, firestore, useMyOrganization, userOrganizationId]);

  // Convert estimates to SelectField options format
  const estimateOptions = [
    { value: "none", label: "No estimate selected" },
    ...estimates
      .sort((a, b) => {
        const nameA = a.projectName || `Estimate ${a.id}`;
        const nameB = b.projectName || `Estimate ${b.id}`;
        return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: "base" });
      })
      .map((estimate) => ({
        value: estimate.id!,
        label: estimate.projectName || `Estimate ${estimate.id}`,
      })),
  ];

  const handleFileUpload = (files: globalThis.File[]) => {
    // Store actual File objects for upload
    setActualFiles((prev) => [...prev, ...files]);

    // Convert native File objects to our File interface for display
    const newFiles: File[] = files.map((file) => ({
      originalname: file.name,
      size: file.size,
      uploadId: `${Date.now()}-${Math.random()}`,
      remotePath: "", // This would be set after actual upload
      mime: file.type,
    }));

    setUploadedFiles((prev) => [...prev, ...newFiles]);
  };

  const removeUploadedFile = (uploadId: string) => {
    const fileIndex = uploadedFiles.findIndex((file) => file.uploadId === uploadId);
    if (fileIndex !== -1) {
      setUploadedFiles((prev) => prev.filter((file) => file.uploadId !== uploadId));
      setActualFiles((prev) => prev.filter((_, index) => index !== fileIndex));
    }
  };

  const handleSubmit = async () => {
    const organizationToUse = useMyOrganization ? userOrganizationId : selectedOrganization;

    if (!organizationToUse || actualFiles.length === 0) {
      toast.error("Please select an organization and upload at least one file");
      return;
    }

    setIsUploading(true);
    try {
      const estimateId = selectedEstimate && selectedEstimate !== "none" ? selectedEstimate : undefined;

      const response = await fileProvider.uploadValueAddFile(actualFiles, organizationToUse, estimateId);

      if (response.success) {
        toast.success(`Successfully uploaded ${response.data.length} file(s)`);
        // Redirect to analytics page
        navigate("/analytics");
      } else {
        toast.error("Upload failed. Please try again.");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Upload failed. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="relative flex h-full w-full flex-col">
      <Header
        leftSection={
          <div className="flex flex-row items-center space-x-2">
            <FileText className="h-5 w-5" />
            <p>Value Add - Insurance Approved Estimate</p>
          </div>
        }
        rightSection={
          <Button
            onClick={handleSubmit}
            disabled={
              (!useMyOrganization && !selectedOrganization) ||
              (useMyOrganization && !userOrganizationId) ||
              uploadedFiles.length === 0 ||
              isUploading
            }
            className="!rounded-lg !bg-[#000] !px-4 !py-2 !text-sm leading-6 !font-medium text-white"
          >
            {isUploading ? "Uploading..." : "Submit"}
          </Button>
        }
      />

      <div className="flex-1 p-6">
        <div className="grid h-full grid-cols-12 gap-6">
          {/* Left Section - Organization & Estimate Picker */}
          <div className="col-span-3 space-y-4">
            {/* Organization Dropdown */}
            <div className="rounded-lg border bg-white p-6 shadow-sm">
              <h3 className="mb-4 text-lg font-semibold">Organization</h3>

              {/* Use My Organization Checkbox */}
              <div className="mb-4 flex items-center space-x-3">
                <CheckBox checked={useMyOrganization} onChange={() => setUseMyOrganization(!useMyOrganization)} />
                <div>
                  <label className="cursor-pointer text-sm font-medium" onClick={() => setUseMyOrganization(!useMyOrganization)}>
                    Use my organization
                  </label>
                  {useMyOrganization && userOrganizationName && <p className="text-sm text-gray-600">Selected: {userOrganizationName}</p>}
                </div>
              </div>

              <Select
                value={useMyOrganization ? userOrganizationId || "" : selectedOrganization}
                onValueChange={setSelectedOrganization}
                disabled={useMyOrganization}
              >
                <SelectTrigger className={`w-full ${useMyOrganization ? "cursor-not-allowed opacity-50" : ""}`}>
                  <SelectValue placeholder="Select organization" />
                </SelectTrigger>
                <SelectContent>
                  {isLoading ? (
                    <SelectItem value="loading" disabled>
                      Loading organizations...
                    </SelectItem>
                  ) : organizations.length > 0 ? (
                    organizations.map((org) => (
                      <SelectItem key={org.organizationId} value={org.organizationId}>
                        {org.organizationName}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="no-orgs" disabled>
                      No organizations available
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Estimate Dropdown */}
            {((useMyOrganization && userOrganizationId) || (!useMyOrganization && selectedOrganization)) && (
              <div className="rounded-lg border bg-white p-6 shadow-sm">
                <h3 className="mb-4 text-lg font-semibold">Estimate (Optional)</h3>

                <Select value={selectedEstimate} onValueChange={setSelectedEstimate} disabled={isLoadingEstimates}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select estimate (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {estimateOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {isLoadingEstimates && <p className="mt-2 text-sm text-gray-500">Loading estimates...</p>}

                {!isLoadingEstimates && estimates.length === 0 && <p className="mt-2 text-sm text-gray-500">No estimates available</p>}
              </div>
            )}
          </div>

          {/* Right Section - File Upload */}
          <div className="col-span-9">
            <div className="rounded-lg border bg-white p-6 shadow-sm">
              <h3 className="mb-4 text-lg font-semibold">Upload Insurance Approved Estimate (PDF)</h3>

              <DragAndDropFileUploader
                title="Insurance Approved Estimate"
                icon={<FileText size={32} className="text-gray-600" />}
                className="mb-6 w-full"
                onChange={handleFileUpload}
              />

              {/* Uploaded Files List */}
              {uploadedFiles.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-md mb-4 font-medium">Uploaded Files</h4>
                  <UploadedFileList files={uploadedFiles} removeUploadedFile={removeUploadedFile} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
